/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Word of the Day Widget
 *
 * Dashboard card that displays a daily SAT vocabulary word with definition,
 * example sentence, and SAT tip. Users can mark words as "knew it" or
 * "new to me" and view previous days' words.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { BookOpen, ChevronDown, ChevronUp, Check } from 'lucide-react'
import vocabService from '../../services/vocabService'
import { createLogger } from '../../utils/logger'

const logger = createLogger('WordOfTheDayWidget')

// Tone styling owned by the component (not the caller) so the two usage sites
// can't drift apart — same pattern as MiniPill's tones map.
const BANNER_TONES = {
  green: {
    active:
      'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-500 dark:border-green-600',
    hover:
      'hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700'
  },
  blue: {
    active:
      'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-500 dark:border-blue-600',
    hover:
      'hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700'
  }
}

/**
 * "I knew this" / "New to me" button used in the banner variant. Extracted to a
 * module-level component so it isn't re-created on every render.
 */
const BannerInteractionButton = ({ response, label, isActive, disabled, tone, onClick }) => {
  const toneClasses = BANNER_TONES[tone] || BANNER_TONES.green
  return (
    <button
      onClick={() => onClick(response)}
      disabled={disabled}
      data-testid={response === 'knew_it' ? 'knew-it-btn' : 'new-to-me-btn'}
      aria-pressed={isActive}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all whitespace-nowrap ${
        isActive
          ? toneClasses.active
          : `bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 ${toneClasses.hover}`
      }`}
    >
      {isActive && <Check className="w-3.5 h-3.5" />}
      {label}
    </button>
  )
}

BannerInteractionButton.propTypes = {
  response: PropTypes.oneOf(['knew_it', 'new_to_me']).isRequired,
  label: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  disabled: PropTypes.bool,
  tone: PropTypes.oneOf(['green', 'blue']).isRequired,
  onClick: PropTypes.func.isRequired
}

const WordOfTheDayWidget = ({ compact = false, variant = 'card' }) => {
  const [word, setWord] = useState(null)
  const [interaction, setInteraction] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showPrevious, setShowPrevious] = useState(false)
  const [previousWords, setPreviousWords] = useState([])
  const [previousLoading, setPreviousLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchWord = async () => {
      try {
        setLoading(true)
        const result = await vocabService.getWordOfTheDay()
        if (result.success) {
          setWord(result.data.word)
          setInteraction(result.data.interaction)
        }
      } catch (err) {
        setError('Failed to load word of the day')
        logger.error('Error fetching word of the day:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchWord()
  }, [])

  const handleInteraction = async (response) => {
    if (!word || submitting) return
    try {
      setSubmitting(true)
      const result = await vocabService.recordInteraction(word.id, response)
      if (result.success) {
        setInteraction(response)
      }
    } catch (err) {
      logger.error('Error recording interaction:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleTogglePrevious = async () => {
    if (!showPrevious && previousWords.length === 0) {
      try {
        setPreviousLoading(true)
        const result = await vocabService.getRecentWords()
        if (result.success) {
          // Filter out today's word from the list
          const filtered = result.data.filter((w) => w.id !== word?.id)
          setPreviousWords(filtered)
        }
      } catch (err) {
        logger.error('Error fetching recent words:', err)
      } finally {
        setPreviousLoading(false)
      }
    }
    setShowPrevious(!showPrevious)
  }

  if (loading) {
    // The banner variant stays out of the layout until content is ready so it
    // doesn't push the page around on load.
    if (variant === 'banner') return null
    return (
      <div className="card" data-testid="wotd-loading">
        <div className="card-body">
          <div className="animate-pulse space-y-3">
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !word) {
    return null
  }

  // Compact "banner" layout — a single horizontal bar that sits at the top of
  // the dashboard alongside the other notification banners.
  if (variant === 'banner') {
    return (
      <div
        data-testid="wotd-banner"
        className="bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2.5 bg-gradient-to-br from-primary-500 to-purple-600 rounded-xl shadow-md shrink-0">
            <BookOpen className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[11px] font-semibold text-primary-600 dark:text-primary-400 uppercase tracking-wide">
                Word of the Day
              </span>
              <span className="text-base font-bold text-gray-900 dark:text-gray-100">
                {word.word}
              </span>
              <span className="text-xs italic text-gray-500 dark:text-gray-400">
                {word.partOfSpeech}
              </span>
            </div>
            <p
              className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 truncate"
              title={word.definition}
            >
              {word.definition}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <BannerInteractionButton
            response="knew_it"
            label="I knew this"
            isActive={interaction === 'knew_it'}
            disabled={submitting}
            onClick={handleInteraction}
            tone="green"
          />
          <BannerInteractionButton
            response="new_to_me"
            label="New to me"
            isActive={interaction === 'new_to_me'}
            disabled={submitting}
            onClick={handleInteraction}
            tone="blue"
          />
        </div>
      </div>
    )
  }

  return (
    <div className="card" data-testid="wotd-widget">
      <div className="card-body space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <BookOpen className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Word of the Day
            </h3>
          </div>
        </div>

        {/* Word */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{word.word}</h2>
          <p className="text-sm italic text-gray-500 dark:text-gray-400 mt-0.5">
            {word.partOfSpeech}
          </p>
        </div>

        {/* Definition */}
        <div>
          <p className="text-gray-700 dark:text-gray-300">{word.definition}</p>
        </div>

        {/* Example Sentence (hidden in compact mode) */}
        {!compact && (
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-300">Example: </span>
              {word.exampleSentence}
            </p>
          </div>
        )}

        {/* SAT Tip (hidden in compact mode) */}
        {!compact && (
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3">
            <p className="text-sm text-primary-700 dark:text-primary-300">
              <span className="font-semibold">SAT Tip: </span>
              {word.satTip}
            </p>
          </div>
        )}

        {/* Interaction Buttons */}
        <div className="flex gap-3">
          <button
            onClick={() => handleInteraction('knew_it')}
            disabled={submitting}
            data-testid="knew-it-btn"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              interaction === 'knew_it'
                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-2 border-green-500 dark:border-green-600'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-green-50 dark:hover:bg-green-900/20 hover:border-green-300 dark:hover:border-green-700'
            }`}
          >
            {interaction === 'knew_it' && <Check className="w-4 h-4" />}I knew this
          </button>
          <button
            onClick={() => handleInteraction('new_to_me')}
            disabled={submitting}
            data-testid="new-to-me-btn"
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
              interaction === 'new_to_me'
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-2 border-blue-500 dark:border-blue-600'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700'
            }`}
          >
            {interaction === 'new_to_me' && <Check className="w-4 h-4" />}
            New to me
          </button>
        </div>

        {/* Previous Words Toggle (hidden in compact mode) */}
        {!compact && (
          <button
            onClick={handleTogglePrevious}
            data-testid="toggle-previous-btn"
            className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            {showPrevious ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            See Previous Words
          </button>
        )}

        {/* Previous Words List */}
        {!compact && showPrevious && (
          <div className="space-y-2" data-testid="previous-words-list">
            {previousLoading ? (
              <div className="animate-pulse space-y-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded" />
                ))}
              </div>
            ) : previousWords.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                No previous words yet
              </p>
            ) : (
              previousWords.map((prevWord) => (
                <div
                  key={prevWord.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {prevWord.word}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {prevWord.date}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      {prevWord.definition}
                    </p>
                  </div>
                  {prevWord.interaction && (
                    <span
                      className={`ml-2 px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${
                        prevWord.interaction === 'knew_it'
                          ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                          : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                      }`}
                    >
                      {prevWord.interaction === 'knew_it' ? 'Knew it' : 'New'}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  )
}

WordOfTheDayWidget.propTypes = {
  compact: PropTypes.bool,
  variant: PropTypes.oneOf(['card', 'banner'])
}

export default WordOfTheDayWidget
